import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import merge from 'lodash/merge';
import StorageUtils from '@spothero/utils/storage';
import EnvironmentUtils from '@spothero/utils/environment';
import {DESTINATION_UPDATE} from 'destination/destination-actions';
import {ROUTER_DATA_LOAD_FULFILLED} from 'router/router-actions';
import {
    SEARCH_REQUEST_SET_TERM,
    SEARCH_REQUEST_SET_LOCATION,
    SEARCH_REQUEST_UPDATE_TIMES
} from 'search-request/search-request-actions';
import {SPOT_UPDATE_SELECTED} from 'spot/spot-actions';
import {
    SPOTS_GET_RADIAL,
    SPOTS_RADIAL_INCREMENT,
    SPOTS_GET_TILED,
    SPOTS_TILED_INCREMENT,
    SPOTS_GET_AIRPORT
} from 'spots/spots-actions';
import {
    SEARCH_GET_AIRPORTS,
    SEARCH_SET_SORT_TYPE,
    SEARCH_TOGGLE_LIST_VIEW,
    SEARCH_TOGGLE_INFO_PANEL_VIEW,
    SEARCH_TOGGLE_FILTERS_VIEW,
    SEARCH_SET_MAP_INSTANCE,
    SEARCH_SET_ZOOM,
    SEARCH_TOGGLE_MOBILE_LIST_VIEW_ACTIVE
} from './search-actions';
import SearchUtils from './search-utils';

const INFO_PANEL_COOKIE = 'sh-hide-info-panel';

export const initialState = {
    isPending: false,
    data: {...SearchUtils.defaultSearchSettings},
    error: null
};

export default function search(state = initialState, {type, payload, meta}) {
    switch (type) {
        case `${SEARCH_GET_AIRPORTS}_FULFILLED`: {
            return {
                ...state,
                data: {
                    ...state.data,
                    airports: payload,
                }
            };
        }

        case SPOT_UPDATE_SELECTED: {
            const {
                data: {
                    breadcrumbs
                }
            } = state;
            const isInfoPanelVisible = (payload.spot)
                ? false
                : (
                    (!breadcrumbs.city || !breadcrumbs.city.cityPageZoom) &&
                    (!breadcrumbs.event || !breadcrumbs.event.description) &&
                    (!breadcrumbs.destination || !breadcrumbs.destination.description)
                )
                    ? false
                    : !StorageUtils.get(INFO_PANEL_COOKIE, 'cookie');

            return {
                ...state,
                data: {
                    ...state.data,
                    isInfoPanelVisible
                }
            };
        }

        case SEARCH_SET_SORT_TYPE: {
            return merge({}, state, {
                data: {
                    sortBy: payload.sortBy
                }
            });
        }

        case SEARCH_REQUEST_SET_LOCATION: {
            const {
                title,
                geolocated
            } = payload;

            return merge({}, state, {
                data: {
                    destinationTitle: title,
                    geolocated,
                    breadcrumbs: {
                        activeBreadcrumb: {
                            label: title
                        }
                    }
                }
            });
        }

        case SEARCH_REQUEST_UPDATE_TIMES: {
            return merge({}, state, {
                data: {
                    isDefaultTimes: false
                }
            });
        }

        case ROUTER_DATA_LOAD_FULFILLED: {
            const {
                search: cleanedSearchState
            } = payload;

            if (cleanedSearchState) {
                return merge({}, state, cleanedSearchState);
            } else {
                return state;
            }
        }

        case `${SPOTS_GET_RADIAL}_PENDING`: {
            return merge({}, state, {
                isPending: true,
                data: {
                    requestIncrements: meta.numRequests,
                    currentIncrement: 1
                },
                error: null
            });
        }

        case `${SPOTS_GET_RADIAL}_FULFILLED`: {
            return merge({}, state, {
                isPending: false,
                data: {
                    currentIncrement: 0
                }
            });
        }

        case `${SPOTS_GET_RADIAL}_REJECTED`: {
            return merge({}, state, {
                isPending: false,
                data: {
                    currentIncrement: 0
                },
                error: payload
            });
        }

        case SPOTS_RADIAL_INCREMENT: {
            return merge({}, state, {
                isPending: false,
                data: {
                    currentIncrement: state.data.currentIncrement + 1
                },
                error: null
            });
        }

        case `${SPOTS_GET_TILED}_PENDING`: {
            return merge({}, state, {
                isPending: true,
                data: {
                    requestIncrements: meta.numRequests,
                    currentIncrement: 1
                },
                error: null
            });
        }

        case `${SPOTS_GET_TILED}_FULFILLED`: {
            return merge({}, state, {
                isPending: false,
                data: {
                    currentIncrement: 0
                }
            });
        }

        case `${SPOTS_GET_TILED}_REJECTED`: {
            return merge({}, state, {
                isPending: false,
                data: {
                    currentIncrement: 0
                },
                error: payload
            });
        }

        case SPOTS_TILED_INCREMENT: {
            return merge({}, state, {
                isPending: false,
                data: {
                    currentIncrement: state.data.currentIncrement + 1
                },
                error: null
            });
        }

        case `${SPOTS_GET_AIRPORT}_PENDING`: {
            return merge({}, state, {
                data: {
                    sortBy: 'featured'
                }
            });
        }

        case SEARCH_TOGGLE_LIST_VIEW: {
            return merge({}, state, {
                data: {
                    isListVisible: (!isUndefined(state.data.isListVisible))
                        ? !state.data.isListVisible
                        : false
                }
            });
        }

        case SEARCH_TOGGLE_INFO_PANEL_VIEW: {
            const isInfoPanelVisible = (payload.isInfoPanelVisible === true);

            if (!isInfoPanelVisible) {
                StorageUtils.set(INFO_PANEL_COOKIE, true, 'cookie');
            } else {
                StorageUtils.set(INFO_PANEL_COOKIE, false, 'cookie');
            }

            return merge({}, state, {
                data: {
                    isInfoPanelVisible
                }
            });
        }

        case SEARCH_TOGGLE_FILTERS_VIEW: {
            return merge({}, state, {
                data: {
                    areFiltersVisible: !state.data.areFiltersVisible
                }
            });
        }

        case SEARCH_TOGGLE_MOBILE_LIST_VIEW_ACTIVE: {
            return merge({}, state, {
                data: {
                    isMobileListViewActive: payload.isMobileListViewActive
                }
            });
        }

        case SEARCH_SET_MAP_INSTANCE: {
            return merge({}, state, {
                data: {
                    searchMapInstance: payload.searchMapInstance
                }
            });
        }

        case SEARCH_SET_ZOOM: {
            return merge({}, state, {
                data: {
                    zoom: payload.zoom
                }
            });
        }

        case DESTINATION_UPDATE: {
            return merge({}, state, {
                data: {
                    searchType: payload.searchType
                }
            });
        }

        case SEARCH_REQUEST_SET_TERM: {
            const {
                monthly,
                city,
                destination = {},
            } = payload;
            const {
                zoom
            } = state;
            const isMobile = EnvironmentUtils.isMobileDevice();
            const hasDestination = !isEmpty(destination);

            const updatedZoom = (city || hasDestination)
                ? (city)
                    ? SearchUtils.getCityZoom({city, monthly, mobile: isMobile})
                    : (hasDestination)
                        ? (destination.override_ideal_zoom && (destination.desktop_zoom_level || destination.mobile_zoom_level))
                            ? SearchUtils.getDestinationZoom({destination, monthly, mobile: isMobile})
                            : destination.default_map_zoom_level || city.default_map_zoom_level
                        : zoom
                : zoom;

            return merge({}, state, {
                data: {
                    zoom: updatedZoom
                }
            });
        }

        default:
            return state;
    }
}
